<template>
  <div data-spec="main">
    <section data-test="product-results-refinement">
      <div class="mb-12 justify-between lg:mb-32 lg:flex">
        <span />
      </div>
      <div class="sticky -top-1 z-[1]">
        <TopFilters
          v-model:filters="filtersInput"
          v-model:price="priceInput"
          v-model:sort="sortInput"
          :facets
          :price-facet
          :search-nb-results="total"
          :sort-options
        />
      </div>

      <div class="mt-16 lg:flex lg:gap-16">
        <TheMainContent class="w-full">
          <span
            class="text-static-default-low body-2 mb-12 block lg:text-caption md:mb-16"
            data-selector="total-products"
            data-test="total-products"
          >
            {{
              i18n(translations.resultsCount, {
                total,
              })
            }}
          </span>
          <SwapPill
            v-if="features.buyback?.tradeInEnabled && !KILL_SWAP_BLOCK_PLP"
            class="lg:hidden"
          />

          <NoResults
            v-if="products.length === 0"
            class="my-32"
            data-spec="body"
          />
          <ResultGrid v-else class="mt-8 md:mt-32" :products />
        </TheMainContent>
      </div>

      <Pagination v-model="pageInput" :page-count />
    </section>

    <FullscreenFilters
      v-model:filters="filtersInput"
      v-model:price="priceInput"
      v-model:sort="sortInput"
      :facets
      :price-facet
      :search-nb-results="total"
      :sort-options
    />
  </div>
</template>

<script setup lang="ts">
import {
  createError,
  useHead,
  useRequestURL,
  useRoute,
  useRouteParams,
  useRuntimeConfig,
} from '#imports'

import FullscreenFilters from '@backmarket/nuxt-layer-recommendation/filtering/FullscreenFilters.vue'
import TopFilters from '@backmarket/nuxt-layer-recommendation/filtering/TopFilters.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'
import SwapPill from '~/scopes/buyback/swap/components/SwapPill/SwapPill.vue'
import NoResults from '~/scopes/product-list/components/NoResults/NoResults.vue'
import Pagination from '~/scopes/product-list/components/Pagination/Pagination.vue'
import ResultGrid from '~/scopes/product-list/components/ResultGrid/ResultGrid.vue'
import { useFiltersFromHash } from '~/scopes/product-list/composables/useHashFilters'
import { useProductsSearch } from '~/scopes/product-list/composables/useProductsSearch'
import {
  getSortOptions,
  useAlgoliaConfiguration,
} from '~/scopes/product-list/helpers/helpers'
import { getMetas } from '~/utils/seo/meta'

import translations from './ItemsTab.translations'

const i18n = useI18n()
const route = useRoute()
const { protocol, hostname } = useRequestURL()
const { uuid: sellerId } = useRouteParams()
const { features } = useMarketplace()
const {
  public: { COMPANY_NAME, KILL_SWAP_BLOCK_PLP },
} = useRuntimeConfig()

const { company } = defineProps<{
  company?: string
}>()

const { data: searchConfig } = await useAlgoliaConfiguration(
  'merchant-landing',
  sellerId,
)

if (searchConfig.value === null) {
  throw createError({
    statusCode: 500,
    statusMessage: 'Unable to retrieve search configuration',
    fatal: true,
  })
}

const sortOptions = getSortOptions(searchConfig.value.indexes)

const {
  total,
  products,
  facets,
  pageCount,
  priceFacet,
  filtersInput,
  sortInput,
  pageInput,
  priceInput,
} = await useProductsSearch(searchConfig.value)

useFiltersFromHash(filtersInput, priceInput, pageInput)

const pageNumber = Array.isArray(route.query.p)
  ? route.query.p[0]
  : route.query.p

useHead({
  title: pageNumber
    ? i18n(translations.landingSellerMetaTitleWithPage, {
        sellerName: company,
        pageNumber: parseInt(pageNumber, 10) + 1,
      })
    : i18n(translations.landingSellerMetaTitleNew, {
        merchantName: company,
      }),
  titleTemplate: `%s | ${COMPANY_NAME}`,
  meta: getMetas({
    content: i18n(translations.landingSellerMetaDescriptionNew, {
      merchantName: company,
    }),
    image: '/img/plp/ogImage.png',
    'og:type': 'website',
  }),
  link: [
    {
      href: `${protocol}//${hostname}${route.path}`,
      rel: 'canonical',
    },
  ],
})
</script>
